import { useQueryRetailerETA } from '@/features/shop/services/DeliveryWindows/queries'
import { Body } from '@shipt/design-system-typography'
import styled from 'styled-components'
import { IconButton } from '@shipt/design-system-buttons'
import { Row } from '@shipt/design-system-layouts'
import { InfoCircleIcon, SameDayDeliveryIcon } from '@shipt/design-system-icons'
import { useHandleDialog } from '@/features/shared/state/Dialog/useHandleDialog'
import { DynamicStoreInfoDrawer } from '@/features/shop/components/Drawer/StoreInfoDrawer/DynamicStoreInfoDrawer'
import { useEffect } from 'react'
import { trackElementViewed } from '@/features/shared/analytics/element'
import { useStoreId } from '@/features/account/services/User/hooks'
import { SkeletonLoader } from '@/features/shared/components/SkeletonLoader'

type Props = {
  isPill?: boolean
  size?: 'sm' | 'md'
  variant?: 'primary' | 'secondary'
  isHamburgerMenu?: boolean
}

export const NextDeliveryWindowText = ({
  isPill,
  size = 'sm',
  variant = 'secondary',
  isHamburgerMenu = false,
}: Props) => {
  const { data: retailerETAs, isLoading } = useQueryRetailerETA()
  const { openDialog } = useHandleDialog()
  const storeId = useStoreId()
  const nextETA = retailerETAs?.delivery_etas[0]
  const promiseType = nextETA?.time_slot.promise_type
  const isExpress = promiseType === 'express'
  const display_sublocation = isHamburgerMenu ? 'main_menu (left side)' : undefined
  useEffect(() => {
    if (isExpress) {
      trackElementViewed({
        content: 'express_delivery',
        store_location_id: Number(nextETA?.store_location_id) || 0,
        store_id: storeId,
        display_sublocation,
      })
    }
  }, [isExpress, nextETA?.store_location_id, storeId, display_sublocation])

  if (isLoading) {
    return (
      <SkeletonLoader isLoading inferSize>
        <DeliveryText size={size}>Loading</DeliveryText>
      </SkeletonLoader>
    )
  }

  if (!nextETA?.time_slot) {
    return null
  }

  const isExpressOrPriority = isExpress || promiseType === 'priority'

  const handleIconClick = () => {
    openDialog(DynamicStoreInfoDrawer, {
      defaultTab: 'storeInfo',
    })
  }

  return (
    <Row align="center" spacing="xxs">
      {!!isExpress && <SameDayDeliveryIcon concept="feedback" size="sm" />}
      <DeliveryText
        strong={isPill || isExpress}
        variant={isPill || isExpress ? 'primary' : variant}
        size={size}
        concept={isExpress ? 'feedback' : 'base'}
      >
        {nextETA.time_slot.formatted_text}
      </DeliveryText>
      {!!isExpressOrPriority && (
        <DeliveryInfoButton
          variant="ghost"
          icon={InfoCircleIcon}
          size="sm"
          onClick={handleIconClick}
          aria-label="Delivery info"
        />
      )}
    </Row>
  )
}

const DeliveryText = styled(Body)`
  min-width: 6.25rem;
`
const DeliveryInfoButton = styled(IconButton)`
  padding: 0;
`
